
const libPictView = require('pict-view');

/**
 * Master List configuration tool.
 *
 * A product-manager-facing screen for managing the reference/catalog lists: add or remove a
 * list, file it under a category, choose which columns it shows (order + header label + search),
 * and choose how it surfaces its connections (read tab / bulk / matrix / unlink). Each change is
 * saved to a chosen scope — Platform Default (Global), Customer, or My Settings (User) — and the
 * Configuration service cascades the layers. All state lives in pict.AppData.MasterListManager
 * and all behavior lives in the MasterListRegistry provider; the view derives + repaints
 * declaratively.
 */
class MasterListManager extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get provider()
	{
		return this.pict.providers.MasterListRegistry;
	}

	deriveRenderState()
	{
		if (this.provider)
		{
			this.provider.deriveRenderState();
		}
	}

	onBeforeRender(pRenderable)
	{
		this.deriveRenderState();
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = MasterListManager;

module.exports.default_configuration =
{
	ViewIdentifier: 'MasterListManager',
	DefaultRenderable: 'MasterListWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: /*css*/`
		.mlm-wrap { padding: 1.25rem 1.5rem 2rem; }
		.mlm-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 1rem; }
		.mlm-list { padding: 0.5rem; }
		.mlm-rail-group { margin-bottom: 0.75rem; }
		.mlm-rail-head { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--bulma-text-weak, #7a7a7a); padding: 0.35rem 0.5rem; display: flex; align-items: center; gap: 0.4rem; }
		.mlm-list-row { display: flex; align-items: center; border-radius: 6px; }
		.mlm-list-row:hover { background: var(--bulma-background, #f5f5f5); }
		.mlm-list-row.is-active { background: var(--theme-color-brand-primary, #2e7d74); }
		.mlm-list-row.is-active .mlm-list-main, .mlm-list-row.is-active .icon { color: #fff; }
		.mlm-list-main { display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 0.6rem; flex: 1 1 auto; cursor: pointer; color: inherit; min-width: 0; }
		.mlm-list-label { font-weight: 600; }
		.mlm-remove { opacity: 0; transition: opacity 0.12s; }
		.mlm-list-row:hover .mlm-remove { opacity: 1; }
		.mlm-add { border-left: 3px solid var(--theme-color-brand-primary, #2e7d74); }
		.mlm-add-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.25rem; }
		.mlm-cand-group { margin-bottom: 0.6rem; }
		.mlm-cand-head { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--bulma-text-weak, #7a7a7a); margin-bottom: 0.3rem; display: flex; align-items: center; gap: 0.4rem; }
		.mlm-cand-chips { display: flex; flex-wrap: wrap; gap: 0.4rem; }
		.mlm-editor-head { display: flex; align-items: flex-end; gap: 1rem; flex-wrap: wrap; }
		.mlm-editor-head .field { margin-bottom: 0; }
		.mlm-editor-head .field:first-child { flex: 1 1 14rem; }
		.mlm-scope label .icon { color: var(--theme-color-brand-primary, #2e7d74); }
		.mlm-editor-actions { display: flex; gap: 0.5rem; margin-left: auto; }
		.mlm-scope-hint { margin-top: 0.6rem; }
		.mlm-cols { margin-top: 0.75rem; }
		.mlm-cols td, .mlm-cols th { vertical-align: middle; }
		.mlm-cols tr.has-text-grey code { color: var(--bulma-text-weak, #9a9a9a); }
		.mlm-order .button { padding-left: 0.5rem; padding-right: 0.5rem; }
		.mlm-assoc { margin-top: 1.5rem; border-top: 1px solid var(--bulma-border, #ededed); padding-top: 1rem; }
		.mlm-assoc-table th, .mlm-assoc-table td { vertical-align: middle; }
		.mlm-assoc-table td code { color: var(--bulma-text-weak, #9a9a9a); }
		.mlm-empty { padding: 2.5rem 1rem; }
		.mlm-spinner { display: inline-block; width: 1.6rem; height: 1.6rem; border: 3px solid var(--bulma-border, #dbdbdb); border-top-color: var(--bulma-link, #485fc7); border-radius: 50%; animation: mlm-spin 0.8s linear infinite; }
		@keyframes mlm-spin { to { transform: rotate(360deg); } }
	`,
	Templates:
	[
		{
			Hash: 'MasterList-Wrap',
			Template: /*html*/`
				<div class="mlm-wrap">
					<div class="mlm-header">
						<div>
							<h1 class="title is-4">Master Lists</h1>
							<p class="subtitle is-6 has-text-grey">Add, remove and categorize reference lists — choose the columns and connections each one shows. Changes save to the scope you pick (platform&nbsp;default, customer, or just&nbsp;you) and cascade on top of each other.</p>
						</div>
						<button class="button is-link" onclick="_Pict.providers.MasterListRegistry.toggleAddPanel()"><span class="icon"><i class="fas fa-plus"></i></span><span>Add list</span></button>
					</div>
					{~TS:MasterList-AddPanel:AppData.MasterListManager.AddOpenSlot~}
					{~TS:MasterList-Message:AppData.MasterListManager.MessageSlot~}
					<div class="columns">
						<div class="column is-one-third">
							<div class="mlm-list box">
								{~TS:MasterList-RailGroup:AppData.MasterListManager.RailGroups~}
							</div>
						</div>
						<div class="column">
							{~TS:MasterList-Loading:AppData.MasterListManager.LoadingSlot~}
							{~TS:MasterList-Editor:AppData.MasterListManager.EditorSlot~}
							{~TS:MasterList-EmptyEditor:AppData.MasterListManager.EmptySlot~}
						</div>
					</div>
				</div>`
		},
		{
			Hash: 'MasterList-RailGroup',
			Template: /*html*/`
				<div class="mlm-rail-group">
					<div class="mlm-rail-head"><span class="icon is-small"><i class="{~D:Record.Icon~}"></i></span><span>{~D:Record.Category~}</span></div>
					{~TS:MasterList-RailRow:Record.Lists~}
				</div>`
		},
		{
			Hash: 'MasterList-RailRow',
			Template: /*html*/`
				<div class="mlm-list-row {~D:Record.RowClass~}">
					<a class="mlm-list-main" onclick="_Pict.providers.MasterListRegistry.selectList('{~D:Record.Entity~}')">
						<span class="icon"><i class="{~D:Record.Icon~}"></i></span>
						<span class="mlm-list-label">{~D:Record.Label~}{~D:Record.CuratedBadge~}</span>
					</a>
					<button class="button is-small is-white mlm-remove" title="Remove this list" onclick="_Pict.providers.MasterListRegistry.removeList('{~D:Record.Entity~}')"><i class="fas fa-trash"></i></button>
				</div>`
		},
		{
			Hash: 'MasterList-AddPanel',
			Template: /*html*/`
				<div class="mlm-add box">
					<div class="mlm-add-head">
						<strong>Add a list</strong>
						<button class="delete" aria-label="close" onclick="_Pict.providers.MasterListRegistry.toggleAddPanel(false)"></button>
					</div>
					<p class="help mb-3">Pick a suggested entity, or type any entity name and choose a category.</p>
					{~TS:MasterList-CandidateGroup:AppData.MasterListManager.CandidateGroups~}
					<div class="field has-addons mt-4">
						<div class="control is-expanded"><input class="input" type="text" placeholder="Entity name (e.g. MixDesign)" value="{~D:AppData.MasterListManager.AddEntity~}" oninput="_Pict.providers.MasterListRegistry.setAddEntity(this.value)" onkeydown="if(event.key==='Enter'){event.preventDefault();_Pict.providers.MasterListRegistry.addFromForm();}" /></div>
						<div class="control"><div class="select"><select onchange="_Pict.providers.MasterListRegistry.setAddDomain(this.value)">{~TS:MasterList-CategoryOption:AppData.MasterListManager.AddCategoryOptions~}</select></div></div>
						<div class="control"><button class="button is-link" onclick="_Pict.providers.MasterListRegistry.addFromForm()">Add</button></div>
					</div>
				</div>`
		},
		{
			Hash: 'MasterList-CandidateGroup',
			Template: /*html*/`
				<div class="mlm-cand-group">
					<div class="mlm-cand-head"><span class="icon is-small"><i class="{~D:Record.Icon~}"></i></span><span>{~D:Record.Category~}</span></div>
					<div class="mlm-cand-chips">{~TS:MasterList-Candidate:Record.Entities~}</div>
				</div>`
		},
		{
			Hash: 'MasterList-Candidate',
			Template: /*html*/`<button class="button is-small is-light" title="Add {~D:Record.Entity~}" onclick="_Pict.providers.MasterListRegistry.addFromCandidate('{~D:Record.Entity~}')"><span class="icon is-small"><i class="{~D:Record.Icon~}"></i></span><span>{~D:Record.Label~}</span></button>`
		},
		{
			Hash: 'MasterList-CategoryOption',
			Template: /*html*/`<option value="{~D:Record.Name~}" {~D:Record.SelectedAttr~}>{~D:Record.Name~}</option>`
		},
		{
			Hash: 'MasterList-ScopeOption',
			Template: /*html*/`<option value="{~D:Record.Key~}" {~D:Record.SelectedAttr~} {~D:Record.DisabledAttr~}>{~D:Record.Label~}</option>`
		},
		{
			Hash: 'MasterList-ScopeHint',
			Template: /*html*/`<div class="notification is-warning is-soft py-2 px-3 mlm-scope-hint"><span class="icon"><i class="fas fa-lock"></i></span> <span>{~D:Record.ScopeHint~}</span></div>`
		},
		{
			Hash: 'MasterList-Editor',
			Template: /*html*/`
				<div class="mlm-editor box">
					<div class="mlm-editor-head">
						<div class="field">
							<label class="label is-small">List title</label>
							<div class="control"><input class="input" type="text" value="{~D:Record.Title~}" onchange="_Pict.providers.MasterListRegistry.setTitle(this.value)" /></div>
						</div>
						<div class="field">
							<label class="label is-small">Category</label>
							<div class="control"><div class="select"><select onchange="_Pict.providers.MasterListRegistry.setEditorDomain(this.value)">{~TS:MasterList-CategoryOption:AppData.MasterListManager.EditorCategoryOptions~}</select></div></div>
						</div>
						<div class="field mlm-scope">
							<label class="label is-small"><span class="icon is-small"><i class="fas fa-layer-group"></i></span> Save to</label>
							<div class="control"><div class="select"><select onchange="_Pict.providers.MasterListRegistry.setScope(this.value)">{~TS:MasterList-ScopeOption:AppData.MasterListManager.ScopeOptions~}</select></div></div>
						</div>
						<div class="mlm-editor-actions">
							<button class="button is-link" {~D:Record.SaveDisabledAttr~} onclick="_Pict.providers.MasterListRegistry.save()"><span class="icon"><i class="fas fa-floppy-disk"></i></span><span>Save</span></button>
							<button class="button is-light" onclick="_Pict.providers.MasterListRegistry.openList('{~D:Record.SelectedEntity~}')"><span class="icon"><i class="fas fa-up-right-from-square"></i></span><span>Preview</span></button>
						</div>
					</div>
					{~TS:MasterList-ScopeHint:AppData.MasterListManager.ScopeHintSlot~}
					<p class="help mt-2">{~D:Record.VisibleCount~} column(s) shown · {~D:Record.SearchCount~} searchable</p>
					<table class="table is-fullwidth is-narrow is-hoverable mlm-cols">
						<thead><tr><th>Show</th><th>Column</th><th>Header label</th><th class="has-text-centered">Search</th><th class="has-text-right">Order</th></tr></thead>
						<tbody>{~TS:MasterList-ColumnRow:AppData.MasterListManager.Columns~}</tbody>
					</table>
					{~TS:MasterList-AssociationSection:AppData.MasterListManager.AssociationSlot~}
					{~TS:MasterList-ChildListSection:AppData.MasterListManager.ChildListSlot~}
				</div>`
		},
		{
			Hash: 'MasterList-ColumnRow',
			Template: /*html*/`
				<tr class="{~D:Record.RowClass~}">
					<td class="has-text-centered"><input type="checkbox" {~D:Record.VisibleAttr~} onchange="_Pict.providers.MasterListRegistry.toggleVisible('{~D:Record.Key~}')" /></td>
					<td><code>{~D:Record.Key~}</code>{~D:Record.SystemBadge~}</td>
					<td><input class="input is-small" type="text" value="{~D:Record.DisplayName~}" onchange="_Pict.providers.MasterListRegistry.setColumnLabel('{~D:Record.Key~}', this.value)" /></td>
					<td class="has-text-centered"><input type="checkbox" {~D:Record.SearchAttr~} onchange="_Pict.providers.MasterListRegistry.toggleSearch('{~D:Record.Key~}')" /></td>
					<td class="has-text-right mlm-order">
						<button class="button is-small is-white" title="Move up" onclick="_Pict.providers.MasterListRegistry.moveUp('{~D:Record.Key~}')"><i class="fas fa-arrow-up"></i></button>
						<button class="button is-small is-white" title="Move down" onclick="_Pict.providers.MasterListRegistry.moveDown('{~D:Record.Key~}')"><i class="fas fa-arrow-down"></i></button>
					</td>
				</tr>`
		},
		{
			Hash: 'MasterList-AssociationSection',
			Template: /*html*/`
				<div class="mlm-assoc">
					<h2 class="label is-small">Connections (associations)</h2>
					<p class="help mb-2">Choose how each related list is surfaced on this one. A connection can appear in more than one place. Read tabs share one layout (below).</p>
					<table class="table is-fullwidth is-narrow mlm-assoc-table">
						<thead><tr><th>Connected list</th><th class="has-text-centered" title="An editor tab on each record">Read&nbsp;tab</th><th class="has-text-centered" title="A bulk assign screen in the menu">Bulk</th><th class="has-text-centered" title="A many-to-many grid screen">Matrix</th><th class="has-text-centered" title="An unlink screen">Unlink</th><th>Label</th></tr></thead>
						<tbody>{~TS:MasterList-AssociationRow:AppData.MasterListManager.Associations~}</tbody>
					</table>
					{~TS:MasterList-ReadLayoutField:AppData.MasterListManager.ReadLayoutSlot~}
				</div>`
		},
		{
			Hash: 'MasterList-AssociationRow',
			Template: /*html*/`
				<tr>
					<td><strong>{~D:Record.OtherLabel~}</strong> <code class="is-size-7">{~D:Record.Hash~}</code></td>
					<td class="has-text-centered"><input type="checkbox" {~D:Record.ReadTabAttr~} onchange="_Pict.providers.MasterListRegistry.setAssociationView('{~D:Record.Hash~}','readtab',this.checked)" /></td>
					<td class="has-text-centered"><input type="checkbox" {~D:Record.BulkAttr~} onchange="_Pict.providers.MasterListRegistry.setAssociationView('{~D:Record.Hash~}','bulk',this.checked)" /></td>
					<td class="has-text-centered"><input type="checkbox" {~D:Record.MatrixAttr~} onchange="_Pict.providers.MasterListRegistry.setAssociationView('{~D:Record.Hash~}','matrix',this.checked)" /></td>
					<td class="has-text-centered"><input type="checkbox" {~D:Record.UnlinkAttr~} onchange="_Pict.providers.MasterListRegistry.setAssociationView('{~D:Record.Hash~}','unlink',this.checked)" /></td>
					<td><input class="input is-small" type="text" value="{~D:Record.Title~}" onchange="_Pict.providers.MasterListRegistry.setAssociationTitle('{~D:Record.Hash~}', this.value)" /></td>
				</tr>`
		},
		{
			Hash: 'MasterList-ReadLayoutField',
			Template: /*html*/`
				<div class="field is-horizontal mt-2">
					<div class="field-label is-small"><label class="label">Read-tab layout</label></div>
					<div class="field-body"><div class="field is-narrow"><div class="control"><div class="select is-small"><select onchange="_Pict.providers.MasterListRegistry.setReadLayout(this.value)">{~TS:MasterList-ReadLayoutOption:AppData.MasterListManager.ReadLayoutOptions~}</select></div></div></div></div>
				</div>`
		},
		{
			Hash: 'MasterList-ReadLayoutOption',
			Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~}>{~D:Record.Value~}</option>`
		},
		{
			Hash: 'MasterList-ChildListSection',
			Template: /*html*/`
				<div class="mlm-assoc">
					<h2 class="label is-small">Child lists (foreign-key)</h2>
					<p class="help mb-2">Filtered lists of records that point back at this one. Each shows as a read-view tab.</p>
					<table class="table is-fullwidth is-narrow mlm-assoc-table">
						<thead><tr><th>Child list</th><th class="has-text-centered" title="A filtered child-record tab on each record">Tab</th><th>Label</th></tr></thead>
						<tbody>{~TS:MasterList-ChildListRow:AppData.MasterListManager.ChildLists~}</tbody>
					</table>
				</div>`
		},
		{
			Hash: 'MasterList-ChildListRow',
			Template: /*html*/`
				<tr>
					<td><strong>{~D:Record.OtherLabel~}</strong></td>
					<td class="has-text-centered"><input type="checkbox" {~D:Record.ChildOnAttr~} onchange="_Pict.providers.MasterListRegistry.setChildListView('{~D:Record.Key~}',this.checked)" /></td>
					<td><input class="input is-small" type="text" value="{~D:Record.Title~}" onchange="_Pict.providers.MasterListRegistry.setChildListTitle('{~D:Record.Key~}', this.value)" /></td>
				</tr>`
		},
		{
			Hash: 'MasterList-Message',
			Template: /*html*/`<div class="notification {~D:Record.MessageClass~} is-soft py-2 px-3 mb-3">{~D:Record.Message~}</div>`
		},
		{
			Hash: 'MasterList-EmptyEditor',
			Template: /*html*/`
				<div class="mlm-empty box has-text-grey has-text-centered">
					<span class="icon is-large"><i class="fas fa-hand-pointer fa-2x"></i></span>
					<p class="mt-3">Pick a list on the left to choose its columns and connections, or “Add list” to manage a new one.</p>
				</div>`
		},
		{
			Hash: 'MasterList-Loading',
			Template: /*html*/`
				<div class="mlm-empty box has-text-grey has-text-centered">
					<span class="mlm-spinner"></span>
					<p class="mt-3">Loading columns…</p>
				</div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'MasterListWrap',
			TemplateHash: 'MasterList-Wrap',
			ContentDestinationAddress: '#PlatformConfiguration-Application-Container'
		}
	]
};
